// ReduxViewModal.jsx
// DAS-1421: Redux state viewer modal for debugging (userid=2 only)

import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Box,
  Typography,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  Autocomplete,
  ListItem,
  ListItemText,
  Chip,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import store from '../redux/store';

const ReduxViewModal = ({ isVisible, onClose }) => {
  const [reduxState, setReduxState] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});
  const [expandedPaths, setExpandedPaths] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [allPathsCache, setAllPathsCache] = useState([]);
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');

  // Debounce search query to avoid excessive recalculations
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Extract all paths from state for search (only once when state loads)
  const extractPaths = React.useCallback((obj, prefix = '', paths = [], depth = 0) => {
    // Limit depth to prevent infinite recursion and performance issues
    if (depth > 8) return paths;
    
    if (obj === null || obj === undefined) {
      if (prefix) paths.push(prefix);
      return paths;
    }
    
    if (typeof obj !== 'object') {
      if (prefix) paths.push(prefix);
      return paths;
    }
    
    if (Array.isArray(obj)) {
      // Limit array processing to first 100 items
      const maxItems = Math.min(obj.length, 100);
      for (let idx = 0; idx < maxItems; idx++) {
        const newPath = `${prefix}[${idx}]`;
        paths.push(newPath);
        extractPaths(obj[idx], newPath, paths, depth + 1);
      }
      return paths;
    }
    
    const keys = Object.keys(obj);
    // Limit object keys processing
    const maxKeys = Math.min(keys.length, 200);
    for (let i = 0; i < maxKeys; i++) {
      const key = keys[i];
      const newPath = prefix ? `${prefix}.${key}` : key;
      paths.push(newPath);
      extractPaths(obj[key], newPath, paths, depth + 1);
    }
    
    return paths;
  }, []);

  // Cache all paths when state loads (only once)
  useEffect(() => {
    if (reduxState && allPathsCache.length === 0) {
      const paths = extractPaths(reduxState);
      setAllPathsCache(paths);
    }
  }, [reduxState, extractPaths, allPathsCache.length]);

  // Generate search suggestions (using debounced query and cached paths)
  const generateSuggestions = useMemo(() => {
    if (!debouncedSearchQuery || allPathsCache.length === 0) return [];
    
    const query = debouncedSearchQuery.toLowerCase();
    
    return allPathsCache
      .filter(path => path.toLowerCase().includes(query))
      .slice(0, 20)
      .map(path => ({
        label: path,
        value: path,
      }));
  }, [allPathsCache, debouncedSearchQuery]);

  // Check if path matches search query (using debounced query)
  const pathMatchesSearch = React.useCallback((path) => {
    if (!debouncedSearchQuery) return false;
    return path.toLowerCase().includes(debouncedSearchQuery.toLowerCase());
  }, [debouncedSearchQuery]);

  // Expand path to show selected item
  const expandPathToSelection = React.useCallback((selectedPath) => {
    if (!selectedPath || !reduxState) return;
    
    // Parse the path more carefully to handle dots, brackets, and array indices
    // Example: "portfolio.data.Market_Positioning_Analysis.columns[0]"
    const parts = [];
    let current = '';
    let inBrackets = false;
    
    for (let i = 0; i < selectedPath.length; i++) {
      const char = selectedPath[i];
      if (char === '[') {
        if (current) {
          parts.push(current);
          current = '';
        }
        inBrackets = true;
      } else if (char === ']') {
        if (current) {
          parts.push(`[${current}]`);
          current = '';
        }
        inBrackets = false;
      } else if (char === '.' && !inBrackets) {
        if (current) {
          parts.push(current);
          current = '';
        }
      } else {
        current += char;
      }
    }
    if (current) {
      parts.push(current);
    }
    
    // Expand all parent sections and paths
    const newExpandedSections = { ...expandedSections };
    const newExpandedPaths = { ...expandedPaths };
    
    // Expand top-level section (first part)
    if (parts.length > 0 && reduxState[parts[0]]) {
      newExpandedSections[parts[0]] = true;
    }
    
    // Build path incrementally and expand each level
    let currentPath = parts[0];
    for (let i = 1; i < parts.length; i++) {
      const part = parts[i];
      // Check if part is an array index (starts with [)
      if (part.startsWith('[') && part.endsWith(']')) {
        const index = part.slice(1, -1);
        currentPath = `${currentPath}[${index}]`;
      } else {
        currentPath = currentPath ? `${currentPath}.${part}` : part;
      }
      newExpandedPaths[currentPath] = true;
    }
    
    setExpandedSections(newExpandedSections);
    setExpandedPaths(newExpandedPaths);
    
    // Scroll to the element after a short delay to allow rendering
    setTimeout(() => {
      // Try exact path first
      let element = document.querySelector(`[data-path="${selectedPath}"]`);
      // If not found, try scrolling to the last expanded path
      if (!element && currentPath) {
        element = document.querySelector(`[data-path="${currentPath}"]`);
      }
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        // Highlight briefly
        const originalBg = element.style.backgroundColor;
        element.style.backgroundColor = 'rgba(25, 118, 210, 0.2)';
        setTimeout(() => {
          element.style.backgroundColor = originalBg || '';
        }, 2000);
      }
    }, 300);
  }, [reduxState, expandedSections, expandedPaths]);

  // Load Redux state
  const loadReduxState = () => {
    setIsLoading(true);
    setSearchQuery(''); // Clear search
    setAllPathsCache([]); // Clear cache
    // Use setTimeout to allow UI to update before processing
    setTimeout(() => {
      try {
        const state = store.getState();
        setReduxState(state);
        // Expand all sections by default
        const sections = {};
        Object.keys(state).forEach(key => {
          sections[key] = true;
        });
        setExpandedSections(sections);
        setExpandedPaths({}); // Reset nested expansions
      } catch (error) {
        console.error('Error loading Redux state:', error);
      } finally {
        setIsLoading(false);
      }
    }, 100);
  };

  // Load state when modal opens
  useEffect(() => {
    if (isVisible) {
      loadReduxState();
    }
  }, [isVisible]);

  // Handle accordion expansion
  const handleAccordionChange = React.useCallback((section) => (event, isExpanded) => {
    event.stopPropagation(); // Prevent event bubbling
    setExpandedSections(prev => ({
      ...prev,
      [section]: isExpanded
    }));
  }, []);

  // Handle nested object expansion
  const handlePathExpansion = React.useCallback((path) => (event, isExpanded) => {
    event.stopPropagation(); // Prevent event bubbling
    setExpandedPaths(prev => ({
      ...prev,
      [path]: isExpanded
    }));
  }, []);

  // Check if path should be expanded (for search highlighting)
  const shouldExpandPath = React.useCallback((path) => {
    // Always respect manual expansion state first
    if (expandedPaths[path] !== undefined) {
      return expandedPaths[path];
    }
    // Auto-expand if search matches
    if (debouncedSearchQuery && pathMatchesSearch(path)) {
      return true;
    }
    return false;
  }, [debouncedSearchQuery, expandedPaths, pathMatchesSearch]);

  // Render nested object/array as collapsible
  const renderNestedValue = (value, path, depth = 0) => {
    if (value === null) return <Typography variant="body2" sx={{ fontFamily: 'monospace', color: '#666' }}>null</Typography>;
    if (value === undefined) return <Typography variant="body2" sx={{ fontFamily: 'monospace', color: '#666' }}>undefined</Typography>;
    
    if (depth > 10) return <Typography variant="body2" sx={{ fontFamily: 'monospace', color: '#999' }}>[Max Depth Reached]</Typography>;
    
    if (typeof value === 'object') {
      if (Array.isArray(value)) {
        if (value.length === 0) {
          return <Typography variant="body2" sx={{ fontFamily: 'monospace', color: '#666' }}>[]</Typography>;
        }
        
        const isExpanded = shouldExpandPath(path);
        const matchesSearch = pathMatchesSearch(path);
        
        return (
          <Accordion
            key={path}
            expanded={isExpanded}
            onChange={handlePathExpansion(path)}
            data-path={path}
            sx={{ 
              mb: 0.5,
              boxShadow: matchesSearch && debouncedSearchQuery ? '0 0 4px rgba(25, 118, 210, 0.5)' : 'none',
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>
                Array({value.length})
              </Typography>
            </AccordionSummary>
            {isExpanded && (
            <AccordionDetails>
              <Box sx={{ ml: 1 }}>
                {value.slice(0, 20).map((item, idx) => (
                  <Box key={idx} sx={{ mb: 1 }}>
                    <Typography variant="body2" component="span" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>
                      [{idx}]:
                    </Typography>
                    <Box sx={{ ml: 2, mt: 0.5 }}>
                      {renderNestedValue(item, `${path}[${idx}]`, depth + 1)}
                    </Box>
                  </Box>
                ))}
                {value.length > 20 && (
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', color: '#999', fontStyle: 'italic' }}>
                    ... and {value.length - 20} more items
                  </Typography>
                )}
              </Box>
            </AccordionDetails>
            )}
          </Accordion>
        );
      } else {
        const keys = Object.keys(value);
        if (keys.length === 0) {
          return <Typography variant="body2" sx={{ fontFamily: 'monospace', color: '#666' }}>{'{}'}</Typography>;
        }
        
        const isExpanded = shouldExpandPath(path);
        const matchesSearch = pathMatchesSearch(path);
        
        return (
          <Accordion
            key={path}
            expanded={isExpanded}
            onChange={handlePathExpansion(path)}
            data-path={path}
            sx={{ 
              mb: 0.5,
              boxShadow: matchesSearch && debouncedSearchQuery ? '0 0 4px rgba(25, 118, 210, 0.5)' : 'none',
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>
                Object({keys.length} keys)
              </Typography>
            </AccordionSummary>
            {isExpanded && (
            <AccordionDetails>
              <Box sx={{ ml: 1 }}>
                {keys.slice(0, 100).map(key => {
                  const keyPath = path ? `${path}.${key}` : key;
                  const keyMatches = pathMatchesSearch(keyPath);
                  
                  return (
                    <Box key={key} sx={{ mb: 1 }} data-path={keyPath}>
                      <Typography 
                        variant="body2" 
                        component="span" 
                        sx={{ 
                          fontFamily: 'monospace', 
                          fontWeight: 'bold',
                          backgroundColor: keyMatches && debouncedSearchQuery ? 'rgba(25, 118, 210, 0.1)' : 'transparent',
                          padding: keyMatches && debouncedSearchQuery ? '2px 4px' : '0',
                          borderRadius: keyMatches && debouncedSearchQuery ? '2px' : '0',
                        }}
                      >
                        {key}:
                      </Typography>
                      <Box sx={{ ml: 2, mt: 0.5 }}>
                        {renderNestedValue(value[key], keyPath, depth + 1)}
                      </Box>
                    </Box>
                  );
                })}
                {keys.length > 100 && (
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', color: '#999', fontStyle: 'italic' }}>
                    ... and {keys.length - 100} more keys
                  </Typography>
                )}
              </Box>
            </AccordionDetails>
            )}
          </Accordion>
        );
      }
    }
    
    // Primitive values
    const displayValue = typeof value === 'string' ? `"${value}"` : String(value);
    const matches = debouncedSearchQuery && displayValue.toLowerCase().includes(debouncedSearchQuery.toLowerCase());
    
    return (
      <Typography 
        variant="body2" 
        sx={{ 
          fontFamily: 'monospace',
          backgroundColor: matches ? 'rgba(25, 118, 210, 0.1)' : 'transparent',
          padding: matches ? '2px 4px' : '0',
          borderRadius: matches ? '2px' : '0',
        }}
      >
        {displayValue}
      </Typography>
    );
  };

  // Render state section
  const renderStateSection = (sectionName, sectionData) => {
    const isExpanded = expandedSections[sectionName] || false;
    const sectionMatches = pathMatchesSearch(sectionName);
    
    return (
      <Accordion
        key={sectionName}
        expanded={isExpanded}
        onChange={handleAccordionChange(sectionName)}
        data-path={sectionName}
        sx={{ 
          mb: 1,
          boxShadow: sectionMatches && debouncedSearchQuery ? '0 0 4px rgba(25, 118, 210, 0.5)' : 'none',
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{ backgroundColor: 'rgba(0, 0, 0, 0.03)' }}
        >
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            {sectionName}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              backgroundColor: '#f5f5f5',
              maxHeight: '400px',
              overflow: 'auto',
              fontFamily: 'monospace',
            }}
          >
            {renderNestedValue(sectionData, sectionName, 0)}
          </Paper>
        </AccordionDetails>
      </Accordion>
    );
  };

  return (
    <Dialog
      open={isVisible}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          minHeight: '80vh',
          maxHeight: '90vh',
        },
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h5" component="div">
              Redux State Viewer
            </Typography>
            <Button
              startIcon={<RefreshIcon />}
              onClick={loadReduxState}
              disabled={isLoading}
              variant="outlined"
              color="primary"
            >
              Refresh
            </Button>
          </Box>
          
          {/* Search with Autocomplete */}
          <Autocomplete
            freeSolo
            options={generateSuggestions}
            getOptionLabel={(option) => typeof option === 'string' ? option : option.label}
            inputValue={searchQuery}
            onInputChange={(event, newInputValue) => {
              setSearchQuery(newInputValue);
            }}
            onChange={(event, newValue) => {
              if (newValue) {
                const selectedPath = typeof newValue === 'string' ? newValue : newValue.value;
                setSearchQuery(selectedPath);
                
                // Auto-expand path when selected
                if (selectedPath) {
                  expandPathToSelection(selectedPath);
                }
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Search data objects and fields..."
                variant="outlined"
                size="small"
                InputProps={{
                  ...params.InputProps,
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'action.active' }} />,
                }}
              />
            )}
            renderOption={(props, option) => (
              <ListItem {...props} key={option.value || option}>
                <ListItemText 
                  primary={option.label || option}
                  primaryTypographyProps={{ 
                    sx: { fontFamily: 'monospace', fontSize: '0.875rem' } 
                  }}
                />
              </ListItem>
            )}
            sx={{ width: '100%' }}
          />
          
          {debouncedSearchQuery && (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Chip 
                label={`${generateSuggestions.length} matches`} 
                size="small" 
                color="primary" 
                variant="outlined"
              />
              <Button
                size="small"
                onClick={() => {
                  setSearchQuery('');
                  setDebouncedSearchQuery('');
                }}
                variant="text"
              >
                Clear
              </Button>
            </Box>
          )}
        </Box>
      </DialogTitle>
      
      <DialogContent dividers>
        {isLoading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '400px',
            }}
          >
            <CircularProgress size={60} />
            <Typography variant="body1" sx={{ ml: 2 }}>
              Loading Redux state...
            </Typography>
          </Box>
        ) : reduxState ? (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Total sections: {Object.keys(reduxState).length}
            </Typography>
            {Object.keys(reduxState).map(sectionName =>
              renderStateSection(sectionName, reduxState[sectionName])
            )}
          </Box>
        ) : (
          <Typography variant="body1" color="error">
            No Redux state available
          </Typography>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose} variant="contained" color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReduxViewModal;

